import { FC } from "react";
import {
  Control,
  UseFormRegister,
  UseFormSetValue,
  UseFormSetError,
  UseFormClearErrors,
} from "react-hook-form";
import FormGroup from "app/components/common/inputs/FormGroup";
import CustomDatePicker from "app/components/common/inputs/CustomDatePicker";
import InputField from "app/components/common/inputs/InputField";
import AutocompleteLocations from "app/components/common/AutocompleteLocations";
import SelectField from "app/components/common/inputs/SelectField";
import { CurrentemploymentOptions } from "app/utils/staticValues";

import { stateOfIssueOptions } from "app/utils/staticValues";

interface Props {
  control: Control<any>;
  errors: any;
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  setError: UseFormSetError<any>;
  clearErrors: UseFormClearErrors<any>;
  occupation: string;
  startDate: string;
  employmentType: string;
  employerPhoneNumber: string;
  addressStreetName: string;
  addressStateName: string;
  addressPostCodeName: string;
  selectedEmploymentType?: string;
  hasNotRequired?: boolean;
}

const DynamicSecondEmployment: FC<Props> = ({
  control,
  errors,
  register,
  setValue,
  setError,
  clearErrors,
  occupation,
  startDate,
  employmentType,
  employerPhoneNumber,
  addressStreetName,
  addressStateName,
  addressPostCodeName,
  selectedEmploymentType,
}) => {
  const getErrorMessage = (fieldName: string) => {
    const fieldNames = fieldName.split(".");
    return fieldNames.reduce((acc, key) => acc && acc[key], errors);
  };
  const isNotRequired =
    selectedEmploymentType === "contractor" ||
    selectedEmploymentType === "self-employed" ||
    selectedEmploymentType === "unemployed_or_retired";

  const isDisabledInputs = selectedEmploymentType === "unemployed_or_retired";

  return (
    <>
      <FormGroup>
        <InputField
          label="Occupation"
          name={occupation}
          control={control}
          type="text"
          required={!isNotRequired}
          placeholder="Designer"
          error={getErrorMessage(occupation)?.message as string}
          className="flex-1"
          rules={{
            pattern: {
              value: /^[A-Za-z\s]+$/,
              message: "Only alphabets are allowed",
            },
          }}
          disabled={isDisabledInputs}
        />
        <SelectField
          label="Employment type"
          name={employmentType}
          control={control}
          options={CurrentemploymentOptions}
          placeholder="Please select employment type"
          error={getErrorMessage(employmentType)?.message as string}
          required={true}
          className="flex-1"
        />
      </FormGroup>
      <FormGroup>
        <CustomDatePicker
          name={startDate}
          control={control}
          label="Start date"
          placeholder="DD/MM/YYYY"
          className="flex-1"
          disabled={isDisabledInputs}
          {...(!isNotRequired ? { isPastDate: true } : {})}
          required={!isNotRequired}
        />

        <InputField
          label="Employer phone number"
          name={employerPhoneNumber}
          control={control}
          type="number"
          required={!isNotRequired}
          disabled={isDisabledInputs}
          placeholder="(201) 555-0124"
          error={getErrorMessage(employerPhoneNumber)?.message as string}
          className="flex-1"
          rules={{
            min: {
              value: 1,
              message: "value must be greater than 0",
            },
          }}
        />
      </FormGroup>
      <FormGroup>
        <AutocompleteLocations
          FieldName={addressStreetName}
          placeholder="1901 Thornridge Cir. Shiloh,"
          register={register}
          setValue={setValue}
          setError={setError}
          label="Address"
          required={!isNotRequired}
          className="flex-1"
          error={getErrorMessage(addressStreetName)?.message as string}
          stateFieldName={addressStateName}
          postCodeFieldName={addressPostCodeName}
          clearErrors={clearErrors}
          disabled={isDisabledInputs}
        />
        <div className="flex-1">
          <FormGroup direction="row">
            <SelectField
              label="State"
              name={addressStateName}
              control={control}
              options={stateOfIssueOptions}
              placeholder="State"
              error={getErrorMessage(addressStateName)?.message as string}
              required={!isNotRequired}
              className="flex-1"
              isDisabled={isDisabledInputs}
            />
            <InputField
              label="Post Code"
              name={addressPostCodeName}
              control={control}
              type="number"
              required={!isNotRequired}
              placeholder="8767"
              error={getErrorMessage(addressPostCodeName)?.message as string}
              className="flex-1"
              rules={{
                min: {
                  value: 1,
                  message: "value must be greater than 0",
                },
              }}
              disabled={isDisabledInputs}
            />
          </FormGroup>
        </div>
      </FormGroup>
    </>
  );
};

export default DynamicSecondEmployment;
